import { ContentState, convertFromHTML, EditorState, } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
/**
 * The editor from the bulk-action mail, with a value that survives being read
 * back (DEV-3323).
 *
 * The group-action modal loads its content through
 * `ContentState.createFromText`, which shows markup as literal characters. It
 * gets away with that because a group message is composed once and never
 * reopened. Every other place this editor is now used holds a saved text that
 * is opened and edited again, so the round trip has to work.
 *
 * Whether a stored value is formatted is decided on how it starts, not on
 * whether an angle bracket appears somewhere in it: a broker who wrote
 * "3 < 5" in a plain text would otherwise have had it parsed as markup and
 * seen most of it disappear. A formatted body always opens with one of the
 * block elements the editor itself writes, which `normalizeBlockType` below
 * keeps to a closed set.
 *
 * The same rule is used by the three repos that read these texts, so it is
 * deliberately literal rather than clever.
 */
var FORMATTED_BODY = /^\s*<(p|h1|h2|ul|ol|blockquote|pre)(\s[^>]*)?>/i;
export var isFormattedBody = function (value) { return FORMATTED_BODY.test(value); };
/**
 * The block types the editor's own toolbar can produce, which are exactly the
 * ones the DEV-3323 tag contract allows.
 */
var ALLOWED_BLOCK_TYPES = [
    "unstyled",
    "header-one",
    "header-two",
    "unordered-list-item",
    "ordered-list-item",
    "blockquote",
    "code-block",
];
var PASTED_HEADINGS = [
    "header-three",
    "header-four",
    "header-five",
    "header-six",
];
/**
 * What a block becomes if the toolbar could not have made it.
 *
 * Pasting from Word or a web page brings in block types the toolbar does not
 * offer, an `h3` heading most readily. Stored as it is, such a text opens
 * with a tag the rule above does not recognize, and the broker would meet
 * their own markup as literal characters the next time they opened it. A
 * deeper heading keeps the look it was pasted for by becoming the deepest the
 * toolbar has; anything else loses its block styling and becomes a paragraph.
 *
 * Fixed here, in the editor, rather than by teaching three repos a wider
 * rule: nothing is then stored that a later reader has to guess about.
 */
var normalizeBlockType = function (type) {
    if (ALLOWED_BLOCK_TYPES.includes(type)) {
        return type;
    }
    return PASTED_HEADINGS.includes(type) ? "header-two" : "unstyled";
};
var withNormalizedBlocks = function (content) {
    var blocks = content.getBlocksAsArray();
    if (blocks.every(function (block) { return normalizeBlockType(block.getType()) === block.getType(); })) {
        return content;
    }
    return ContentState.createFromBlockArray(blocks.map(function (block) {
        var type = normalizeBlockType(block.getType());
        return type === block.getType()
            ? block
            : block.set("type", type);
    }), content.getEntityMap());
};
var parseBlocks = function (html) {
    var parsed = convertFromHTML(html);
    return parsed.contentBlocks || [];
};
/**
 * A block with nothing in it, carrying a fresh key. Built through draft-js
 * rather than by hand so the key generator and the empty character list stay
 * its business.
 */
var emptyBlock = function () { return ContentState.createFromText("").getFirstBlock(); };
/**
 * Whether this is a paragraph the broker left blank, rather than one holding
 * something invisible to a reader but meaningful to the editor.
 */
var isEmptyElement = function (element) { var _a; return !((_a = element.textContent) === null || _a === void 0 ? void 0 : _a.trim()) && !element.querySelector("img, hr, table"); };
/**
 * The blocks of a saved text, with its blank lines still in it.
 *
 * draft-js' own HTML parser throws away a paragraph that holds nothing, and
 * a non-breaking space does not save it either: the parser counts that as
 * whitespace and drops the paragraph all the same. So a text saved with blank
 * lines between its paragraphs came back without them, and saving it again
 * made the loss permanent (Jonas, 2026-10-03).
 *
 * The parser is therefore handed one top-level element at a time, and an
 * element that is blank becomes an empty block instead of nothing. Seeding
 * those paragraphs with an invisible character would have been shorter, but
 * it survives into the mail the candidate receives and into every rule that
 * reads the text, the marker check included.
 */
var parseBlocksKeepingBlankLines = function (html) {
    if (typeof DOMParser === "undefined") {
        return parseBlocks(html);
    }
    var body = new DOMParser().parseFromString(html, "text/html").body;
    if (!body.children.length) {
        return parseBlocks(html);
    }
    var blocks = [];
    var loose = "";
    var flushLoose = function () {
        if (loose.trim()) {
            blocks.push.apply(blocks, parseBlocks(loose));
        }
        loose = "";
    };
    body.childNodes.forEach(function (node) {
        var element = node.nodeType === 1 ? node : undefined;
        if (!element) {
            // Text sitting between two elements, which the parser can only read in
            // context, so it is collected and handed over as it is met.
            loose += node.textContent || "";
            return;
        }
        flushLoose();
        if (isEmptyElement(element)) {
            blocks.push(emptyBlock());
            return;
        }
        blocks.push.apply(blocks, parseBlocks(element.outerHTML));
    });
    flushLoose();
    return blocks.length ? blocks : parseBlocks(html);
};
export var htmlToEditorState = function (value) {
    if (!value) {
        return EditorState.createEmpty();
    }
    if (!isFormattedBody(value)) {
        return EditorState.createWithContent(ContentState.createFromText(value));
    }
    // No entity map: the DEV-3323 contract allows no tag that carries one (no
    // link, no image), and the per-element parse above has no single map to
    // hand over. A tag that needed one would have been stripped before it got
    // here.
    return EditorState.createWithContent(withNormalizedBlocks(ContentState.createFromBlockArray(parseBlocksKeepingBlankLines(value))));
};
/**
 * An empty editor still serializes to markup such as `<p><br></p>`, which
 * would read as a filled-in text to every rule downstream, the required one
 * included. Empty in means empty out.
 */
export var editorStateToHtml = function (state) {
    var content = state.getCurrentContent();
    if (!content.hasText()) {
        return "";
    }
    // Normalized on the way out as well as on the way in, because a paste into
    // a live editor never passes through the reader above.
    return stateToHTML(withNormalizedBlocks(content));
};
